// src/services/sanitaryRuleService.js
// =============================================================================
// Frontend API wrapper for Sanitary consumption rules
// =============================================================================

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

export const SANITARY_RULE_TYPES = Object.freeze({
  WORKING_DAY: "working_day",
  PER_VISIT: "per_visit",
  PER_SERVICE: "per_service",
  PER_STATION: "per_station",
});

export const SANITARY_RULE_STATUSES = Object.freeze({
  DRAFT: "draft",
  ACTIVE: "active",
  INACTIVE: "inactive",
  ARCHIVED: "archived",
});

export const SANITARY_DAILY_PRORATION_MODES = Object.freeze({
  NONE: "none",
  BY_OPERATING_HOURS: "by_operating_hours",
});

export const SANITARY_QUANTITY_BASES = Object.freeze({
  DIRECT_PRODUCT: "direct_product",
  WORKING_SOLUTION: "working_solution",
});

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitaryRuleService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "ID_MISSING";
    throw e;
  }
}

function buildParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

function cleanOptionalText(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  return s || null;
}

function cleanNumberLike(value) {
  if (value === undefined || value === null || `${value}`.trim() === "") return null;
  return String(value).replace(",", ".");
}

export function normalizeRulePayload(payload = {}) {
  const basis = payload.quantity_basis || SANITARY_QUANTITY_BASES.DIRECT_PRODUCT;
  return {
    code: cleanOptionalText(payload.code),
    name: cleanOptionalText(payload.name),
    rule_type: payload.rule_type,
    status: payload.status || SANITARY_RULE_STATUSES.DRAFT,
    product_id: basis === SANITARY_QUANTITY_BASES.DIRECT_PRODUCT ? (payload.product_id || null) : null,
    location_id: payload.location_id || null,
    requires_confirmation: Boolean(payload.requires_confirmation),
    posts_to_stock: Boolean(payload.posts_to_stock),
    comment: cleanOptionalText(payload.comment),
    valid_from: payload.valid_from || null,
    valid_to: payload.valid_to || null,
    quantity: basis === SANITARY_QUANTITY_BASES.DIRECT_PRODUCT ? cleanNumberLike(payload.quantity) : null,
    unit_code: basis === SANITARY_QUANTITY_BASES.DIRECT_PRODUCT ? cleanOptionalText(payload.unit_code) : null,
    quantity_basis: basis,
    concentration_percent: null,
    solution_definition_id: basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION ? (payload.solution_definition_id || null) : null,
    solution_execution_count: basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION ? (cleanNumberLike(payload.solution_execution_count) || "1") : null,
    service_id: payload.service_id || null,
    station_code: cleanOptionalText(payload.station_code),
    station_label: cleanOptionalText(payload.station_label),
    calendar_policy: payload.calendar_policy || "working_days_only",
    visit_status_policy: payload.visit_status_policy || "completed_or_checked",
    daily_proration_mode: payload.daily_proration_mode || SANITARY_DAILY_PRORATION_MODES.NONE,
    rule_config_json: payload.rule_config_json || null,
    version_comment: cleanOptionalText(payload.version_comment),
  };
}

export function normalizeVersionPayload(payload = {}) {
  const basis = payload.quantity_basis || SANITARY_QUANTITY_BASES.DIRECT_PRODUCT;
  return {
    valid_from: payload.valid_from || null,
    valid_to: payload.valid_to || null,
    quantity: basis === SANITARY_QUANTITY_BASES.DIRECT_PRODUCT ? cleanNumberLike(payload.quantity) : null,
    unit_code: basis === SANITARY_QUANTITY_BASES.DIRECT_PRODUCT ? cleanOptionalText(payload.unit_code) : null,
    quantity_basis: basis,
    concentration_percent: null,
    solution_definition_id: basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION ? (payload.solution_definition_id || null) : null,
    solution_execution_count: basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION ? (cleanNumberLike(payload.solution_execution_count) || "1") : null,
    location_id: payload.location_id || null,
    service_id: payload.service_id || null,
    station_code: cleanOptionalText(payload.station_code),
    station_label: cleanOptionalText(payload.station_label),
    calendar_policy: payload.calendar_policy || "working_days_only",
    visit_status_policy: payload.visit_status_policy || "completed_or_checked",
    daily_proration_mode: payload.daily_proration_mode || SANITARY_DAILY_PRORATION_MODES.NONE,
    rule_config_json: payload.rule_config_json || null,
    comment: cleanOptionalText(payload.comment),
  };
}

export function normalizeRuleHeaderPayload(payload = {}) {
  return {
    code: cleanOptionalText(payload.code),
    name: cleanOptionalText(payload.name),
    status: payload.status || SANITARY_RULE_STATUSES.DRAFT,
    requires_confirmation: Boolean(payload.requires_confirmation),
    posts_to_stock: Boolean(payload.posts_to_stock),
    comment: cleanOptionalText(payload.comment),
  };
}

export function normalizeRuleEditorPayload(payload = {}) {
  return {
    ...normalizeRulePayload(payload),
    editor_mode: payload.editor_mode || "update_current",
    version_id: payload.version_id || null,
  };
}

export async function listSanitaryRules(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/rules", {
      withCredentials: true,
      params: buildParams(params),
    });
    const data = res?.data;
    if (Array.isArray(data)) {
      return {
        items: data,
        pagination: { page: 1, page_size: data.length || 25, total: data.length, page_count: 1 },
      };
    }
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      pagination: data?.pagination || { page: 1, page_size: 25, total: 0, page_count: 1 },
    };
  } catch (err) {
    throwApiError("GET /sanitary/rules", err);
  }
}

export async function getSanitaryRule(ruleId) {
  assertId("getSanitaryRule", ruleId);
  try {
    const res = await apiClient.get(`/sanitary/rules/${ruleId}`, {
      withCredentials: true,
    });
    return res?.data;
  } catch (err) {
    throwApiError(`GET /sanitary/rules/${ruleId}`, err);
  }
}

export async function createSanitaryRule(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/rules", normalizeRulePayload(payload), {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError("POST /sanitary/rules", err);
  }
}

export async function updateSanitaryRule(ruleId, payload = {}) {
  assertId("updateSanitaryRule", ruleId);
  try {
    const res = await apiClient.put(`/sanitary/rules/${ruleId}`, normalizeRuleHeaderPayload(payload), {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PUT /sanitary/rules/${ruleId}`, err);
  }
}

export async function saveSanitaryRuleEditor(ruleId, payload = {}) {
  assertId("saveSanitaryRuleEditor", ruleId);
  try {
    const res = await apiClient.put(
      `/sanitary/rules/${ruleId}/editor`,
      normalizeRuleEditorPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError(`PUT /sanitary/rules/${ruleId}/editor`, err);
  }
}

export async function setSanitaryRuleStatus(ruleId, status) {
  assertId("setSanitaryRuleStatus", ruleId);
  try {
    const res = await apiClient.patch(
      `/sanitary/rules/${ruleId}/status`,
      { status },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError(`PATCH /sanitary/rules/${ruleId}/status`, err);
  }
}

export async function deleteSanitaryRule(ruleId) {
  assertId("deleteSanitaryRule", ruleId);
  try {
    const res = await apiClient.delete(`/sanitary/rules/${ruleId}`, {
      withCredentials: true,
    });
    return res?.data;
  } catch (err) {
    throwApiError(`DELETE /sanitary/rules/${ruleId}`, err);
  }
}

export async function createSanitaryRuleVersion(ruleId, payload = {}) {
  assertId("createSanitaryRuleVersion", ruleId);
  try {
    const res = await apiClient.post(
      `/sanitary/rules/${ruleId}/versions`,
      normalizeVersionPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError(`POST /sanitary/rules/${ruleId}/versions`, err);
  }
}

export default {
  listSanitaryRules,
  getSanitaryRule,
  createSanitaryRule,
  updateSanitaryRule,
  saveSanitaryRuleEditor,
  setSanitaryRuleStatus,
  deleteSanitaryRule,
  createSanitaryRuleVersion,
};
